import type { ReactNode } from 'react';
import { ArrowSquareOutIcon } from '@phosphor-icons/react';
import { X } from 'lucide-react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@voll-intelligence/ui-kit/ui/tooltip';

/** A ação "abrir em nova aba" do cabeçalho (#5893): o host a passa só onde há
 *  abas (desktop web) — ausente, o cabeçalho fica sem o ↗. Rótulo e callback
 *  vêm do host, como o coração. */
export type HotelDetailsOpenInNewTab = {
  label: string;
  onOpen(): void;
};

/** O botão-ícone do cabeçalho do detalhe (✕, ♥, ↗): fantasma, com o rótulo
 *  ACESSÍVEL repetido no tooltip (#5893) — a mesma frase, porque o tooltip só
 *  mostra a quem vê o que o leitor de tela já anuncia. */
export function HotelDetailsHeaderButton({
  label,
  className,
  size = 'icon-sm',
  pressed,
  disabled = false,
  onClick,
  children,
}: {
  readonly label: string;
  readonly className: string;
  readonly size?: 'icon-sm' | 'icon-lg';
  readonly pressed?: boolean;
  readonly disabled?: boolean;
  readonly onClick: () => void;
  readonly children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size={size}
            className={className}
            {...(pressed === undefined ? {} : { 'aria-pressed': pressed })}
            aria-label={label}
            disabled={disabled}
            onClick={onClick}
          />
        }
      >
        {children}
      </TooltipTrigger>
      <TooltipContent side="bottom">{label}</TooltipContent>
    </Tooltip>
  );
}

export function HotelDetailsCloseButton({
  label,
  onClose,
}: {
  readonly label: string;
  readonly onClose: () => void;
}) {
  return (
    <HotelDetailsHeaderButton label={label} className="hui-hotel-details-close" onClick={onClose}>
      <X />
    </HotelDetailsHeaderButton>
  );
}

/** O ↗, à direita do coração: chama o caminho de aba nova do host, que abre a
 *  mesma página com a busca preservada e deixa esta aba como estava. */
export function HotelDetailsNewTabButton({
  openInNewTab,
  size = 'icon-sm',
}: {
  readonly openInNewTab: HotelDetailsOpenInNewTab;
  readonly size?: 'icon-sm' | 'icon-lg';
}) {
  return (
    <HotelDetailsHeaderButton
      label={openInNewTab.label}
      className="hui-hotel-details-new-tab"
      size={size}
      onClick={() => {
        openInNewTab.onOpen();
      }}
    >
      <ArrowSquareOutIcon aria-hidden="true" />
    </HotelDetailsHeaderButton>
  );
}
